import React, { useEffect, useState } from 'react';
import { FileText, Download } from 'lucide-react';

const isImage = (ct) => (ct || '').startsWith('image/');
const prettySize = (n) => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

// One attachment. The bytes are fetched AUTHENTICATED (Bearer header) into a
// blob URL — a bare <img src="/api/..."> can't send the token — and the blob
// URL is revoked on unmount so we don't leak object URLs.
const AttachmentItem = ({ att, fetchBlob }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const image = isImage(att.content_type);

  useEffect(() => {
    let objectUrl = null;
    let alive = true;
    (async () => {
      try {
        const res = await fetchBlob(att.id);
        objectUrl = URL.createObjectURL(res.data);
        if (alive) setUrl(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      } catch (e) {
        if (alive) setError(true);
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [att.id, fetchBlob]);

  if (image) {
    if (error) {
      return <div className="text-xs text-muted-foreground">Couldn't load {att.filename}</div>;
    }
    return url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={att.filename}
          title={att.filename}
          className="max-h-48 rounded-lg border border-border object-contain bg-secondary/30"
        />
      </a>
    ) : (
      <div className="h-24 w-32 rounded-lg border border-border bg-secondary/30 animate-pulse" />
    );
  }

  // PDF (or any non-image): a download chip. Fetched as a blob, then saved.
  const download = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = att.filename || 'attachment.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={!url}
      title={att.filename}
      className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-border hover:bg-secondary/40 transition disabled:opacity-50"
    >
      <FileText className="w-4 h-4 text-primary shrink-0" />
      <span className="truncate max-w-[200px]">{att.filename}</span>
      <span className="text-muted-foreground">{prettySize(att.size_bytes)}</span>
      <Download className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </button>
  );
};

// Renders all attachments on a message. `fetchBlob(id)` returns the axios
// promise for the authenticated blob (supportApi.getAttachment on the customer
// side, superadminApi.getSupportAttachment on the console side).
const AttachmentList = ({ attachments, fetchBlob }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2 items-start">
      {attachments.map((att) => (
        <AttachmentItem key={att.id} att={att} fetchBlob={fetchBlob} />
      ))}
    </div>
  );
};

export default AttachmentList;
